import { useTimeZoneContext } from '@ais-ui/utils';
import { useGetExtraJobData } from '@ais-ui/websockets';
import { useEffect, useState } from 'react';
import { useMapPanelContext } from '../../Map/MapPanel';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { transformExtent } from 'ol/proj';

type BoundingBox = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

export const useGetReportTableData = (
  jobId: string | undefined,
  filterByMapView: boolean,
) => {
  const { timeZone } = useTimeZoneContext();
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const { setCenterComponentData } = useMapPanelContext();
  const [boundingBox, setBoundingBox] = useState<BoundingBox>({
    minLat: 360,
    maxLat: -360,
    minLon: 360,
    maxLon: -360,
  });

  const { data: shipData } = useGetExtraJobData(
    jobId,
    timeZone,
    filterByMapView ? boundingBox.minLat : 360,
    filterByMapView ? boundingBox.maxLat : -360,
    filterByMapView ? boundingBox.minLon : 360,
    filterByMapView ? boundingBox.maxLon : -360,
  );

  useEffect(() => {
    if (map) {
      const updateBoundingBox = () => {
        const view = map.getView();
        const extent = view.calculateExtent(map.getSize());
        const [minLon, minLat, maxLon, maxLat] = transformExtent(
          extent,
          displayProjection,
          dataProjection,
        );
        setBoundingBox({ minLat, maxLat, minLon, maxLon });
      };

      map.on('moveend', updateBoundingBox);

      // Initial bounding box update when the map is first available
      updateBoundingBox();

      return () => {
        map.un('moveend', updateBoundingBox);
      };
    }
  }, [map]);

  useEffect(() => {
    // Only set data if we have actual content - don't overwrite existing data with empty arrays
    //  had issues with this in CLASSIC-9259 for GAZ systems
    if (shipData && Array.isArray(shipData) && shipData.length > 0) {
      setCenterComponentData(shipData);
    }
    // If shipData is undefined, null, or empty array, do nothing - let other data sources handle it
  }, [shipData, setCenterComponentData]);
};
